<script lang="ts">
	import { onMount } from 'svelte';
	import { push } from 'svelte-spa-router';
	import Navbar from '../../components/Navbar.svelte';
	import AdminSidebar from '../../components/dashboard/AdminSidebar.svelte';
	import { appStore } from '../../stores/appStore';
	import { get } from 'svelte/store';

	let isAdmin = false;
	let loading = $state(false);
	let currentPage = $state(1);
	let totalPages = $state(1);
	let totalCount = $state(0);
	let limit = 20;
	let error = $state('');
	let sessions: any[] = $state([]);
	let revokingId = $state<string | null>(null);

	onMount(async () => {
		const profile = get(appStore).profile;
		if (!profile || profile.userRole !== 'admin') {
			push('/');
			return;
		}
		isAdmin = true;
		await fetchSessions();
	});

	async function fetchSessions() {
		try {
			loading = true;
			error = '';
			const client = get(appStore).client;
			const token = get(appStore).token;

			if (!client || !token) {
				error = 'Not authenticated';
				return;
			}

			const response = await client.getAllSessions({
				token,
				page: currentPage,
				limit
			});

			if (response.response.successFull) {
				sessions = response.response.sessions.map(s => ({
					id: s.id,
					userEmail: s.userEmail,
					browserName: s.browserName,
					osName: s.osName,
					location: s.location,
					lastAccessedAt: formatRelativeTime(s.lastAccessedAt),
					ipAddress: s.ipAddress,
					isActive: s.isActive
				}));
				totalCount = response.response.totalCount;
				totalPages = response.response.totalPages;
			} else {
				error = response.response.reason || 'Failed to load sessions';
			}
		} catch (e) {
			console.error('Error fetching sessions:', e);
			error = 'Failed to load sessions';
		} finally {
			loading = false;
		}
	}

	async function revokeSession(sessionId: string) {
		if (!confirm('Are you sure you want to revoke this session?')) {
			return;
		}

		try {
			revokingId = sessionId;
			const client = get(appStore).client;
			const token = get(appStore).token;

			if (!client || !token) {
				error = 'Not authenticated';
				return;
			}

			const response = await client.revokeSessionAdmin({
				token,
				sessionId
			});

			if (response.response.successFull) {
				// Refresh the sessions list
				await fetchSessions();
			} else {
				alert(response.response.reason || 'Failed to revoke session');
			}
		} catch (e) {
			console.error('Error revoking session:', e);
			alert('Failed to revoke session');
		} finally {
			revokingId = null;
		}
	}

	function goToPage(page: number) {
		currentPage = page;
		fetchSessions();
	}

	function formatRelativeTime(timestamp: any): string {
		if (!timestamp) return 'Unknown';
		const date = new Date(timestamp.seconds * 1000);
		const now = new Date();
		const diff = now.getTime() - date.getTime();
		const minutes = Math.floor(diff / 60000);
		const hours = Math.floor(diff / 3600000);
		const days = Math.floor(diff / 86400000);

		if (minutes < 1) return 'Just now';
		if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
		if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
		return `${days} day${days > 1 ? 's' : ''} ago`;
	}
</script>

<Navbar />

<div class="page-wrapper">
	{#if isAdmin}
		<div class="page-layout">
			<AdminSidebar />
			<div class="page-main">
				<div class="page-header">
					<h1>Active Sessions</h1>
					<p>Monitor and manage user sessions across the platform</p>
				</div>

				<div class="content-card">
					<div class="card-header">
						<h2>All Sessions</h2>
						<span class="count">{totalCount} active sessions</span>
					</div>

					{#if loading}
						<div class="loading">Loading sessions...</div>
					{:else if error}
						<div class="error">{error}</div>
					{:else if sessions.length === 0}
						<div class="empty-state">No active sessions found</div>
					{:else}
						<div class="table-container">
							<table class="data-table">
								<thead>
									<tr>
										<th>User</th>
										<th>Browser</th>
										<th>OS</th>
										<th>Location</th>
										<th>IP Address</th>
										<th>Last Active</th>
										<th>Actions</th>
									</tr>
								</thead>
								<tbody>
									{#each sessions as session}
										<tr>
											<td>{session.userEmail}</td>
											<td>{session.browserName}</td>
											<td>{session.osName}</td>
											<td>{session.location}</td>
											<td class="mono">{session.ipAddress}</td>
											<td>{session.lastAccessedAt}</td>
											<td>
												<button
													class="action-btn danger"
													disabled={revokingId === session.id}
													onclick={() => revokeSession(session.id)}
												>
													{revokingId === session.id ? 'Revoking...' : 'Revoke'}
												</button>
											</td>
										</tr>
									{/each}
								</tbody>
							</table>
						</div>

						{#if totalPages > 1}
							<div class="pagination">
								<button
									class="page-btn"
									disabled={currentPage === 1}
									onclick={() => goToPage(currentPage - 1)}
								>
									Previous
								</button>
								<span class="page-info">
									Page {currentPage} of {totalPages}
								</span>
								<button
									class="page-btn"
									disabled={currentPage === totalPages}
									onclick={() => goToPage(currentPage + 1)}
								>
									Next
								</button>
							</div>
						{/if}
					{/if}
				</div>
			</div>
		</div>
	{/if}
</div>

<style>
	.page-wrapper {
		min-height: 100vh;
		background: var(--bg-primary, #0d1117);
	}

	.page-layout {
		display: flex;
		min-height: calc(100vh - 60px);
	}

	.page-main {
		flex: 1;
		padding: 2rem;
		overflow-y: auto;
	}

	.page-header {
		margin-bottom: 2rem;
	}

	.page-header h1 {
		font-size: 2rem;
		font-weight: 600;
		margin: 0 0 0.5rem 0;
		color: var(--text-primary, #e6edf3);
	}

	.page-header p {
		color: var(--text-secondary, #8b949e);
		margin: 0;
	}

	.content-card {
		background: var(--bg-secondary, #161b22);
		border: 1px solid var(--border-primary, #30363d);
		border-radius: 8px;
		padding: 1.5rem;
	}

	.card-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 1.5rem;
		padding-bottom: 1rem;
		border-bottom: 1px solid var(--border-primary, #30363d);
	}

	.card-header h2 {
		font-size: 1.25rem;
		font-weight: 600;
		margin: 0;
		color: var(--text-primary, #e6edf3);
	}

	.count {
		color: var(--text-secondary, #8b949e);
		font-size: 0.875rem;
	}

	.table-container {
		overflow-x: auto;
	}

	.data-table {
		width: 100%;
		border-collapse: collapse;
	}

	.data-table thead th {
		text-align: left;
		padding: 0.75rem;
		font-size: 0.875rem;
		font-weight: 600;
		color: var(--text-secondary, #8b949e);
		border-bottom: 1px solid var(--border-primary, #30363d);
	}

	.data-table tbody td {
		padding: 0.75rem;
		border-bottom: 1px solid var(--border-secondary, #21262d);
		color: var(--text-primary, #e6edf3);
		font-size: 0.875rem;
	}

	.data-table tbody tr:hover {
		background: var(--bg-tertiary, #1c2128);
	}

	.mono {
		font-family: 'Courier New', monospace;
		font-size: 0.8125rem;
	}

	.action-btn {
		padding: 0.375rem 0.75rem;
		border-radius: 4px;
		border: 1px solid var(--border-primary, #30363d);
		background: var(--bg-tertiary, #1c2128);
		color: var(--text-primary, #e6edf3);
		font-size: 0.8125rem;
		cursor: pointer;
		transition: all 0.2s ease;
	}

	.action-btn.danger:hover {
		background: rgba(248, 81, 73, 0.1);
		border-color: #f85149;
		color: #f85149;
	}

	.action-btn:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}

	.loading,
	.error,
	.empty-state {
		padding: 2rem;
		text-align: center;
		color: var(--text-secondary, #8b949e);
		font-size: 0.875rem;
	}

	.error {
		color: #f85149;
	}

	.pagination {
		display: flex;
		justify-content: center;
		align-items: center;
		gap: 1rem;
		margin-top: 1.5rem;
		padding-top: 1.5rem;
		border-top: 1px solid var(--border-primary, #30363d);
	}

	.page-btn {
		padding: 0.5rem 1rem;
		border-radius: 6px;
		border: 1px solid var(--border-primary, #30363d);
		background: var(--bg-tertiary, #1c2128);
		color: var(--text-primary, #e6edf3);
		font-size: 0.875rem;
		cursor: pointer;
		transition: all 0.2s ease;
	}

	.page-btn:hover:not(:disabled) {
		background: var(--accent-primary, #238636);
		border-color: var(--accent-primary, #238636);
	}

	.page-btn:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}

	.page-info {
		color: var(--text-secondary, #8b949e);
		font-size: 0.875rem;
	}
</style>
